/**
 * What the columns add up to.
 *
 * A bar rather than a row pinned inside the grid. The grid virtualises its
 * columns, so a totals row would have to be positioned against the same moving
 * window as the body and kept in step with it while scrolling sideways — a lot
 * of coupling for a line of figures. Listed by name, it reads the same wherever
 * the grid happens to be scrolled to.
 *
 * Only columns holding numbers appear. A column of postcodes has no total, and
 * printing one would be worse than printing nothing.
 */
import { Badge, Group, Loader, Paper, Text } from '@mantine/core';
import { TOKENS, formatNumber } from '../theme';

type ColumnStats = {
  col: string;
  numeric: number;
  empty: number;
  sum?: number | null;
  min?: number | null;
  max?: number | null;
};

/** Whole numbers stay whole; anything fractional keeps two places. */
function num(v: number | null | undefined): string {
  if (v == null) return '—';
  return Number.isInteger(v) ? formatNumber(v) : v.toFixed(2);
}

export function SheetTotalsBar({
  columns,
  rows,
  loading,
  error,
}: {
  columns: ColumnStats[];
  rows: number;
  loading: boolean;
  error?: string;
}) {
  const numeric = columns.filter((c) => c.numeric > 0);
  const gaps = columns.filter((c) => c.numeric === 0 && c.empty > 0);

  return (
    <Paper withBorder p={6} radius="sm">
      <Group gap="sm" wrap="wrap">
        <Text size="xs" c="dimmed">Across {formatNumber(rows)} rows</Text>
        {loading && <Loader size="xs" />}
        {error && <Text size="xs" c={TOKENS.redText}>{error}</Text>}

        {!loading && numeric.length === 0 && !error && (
          <Text size="xs" c="dimmed">No columns in this file hold numbers.</Text>
        )}

        {numeric.map((c) => (
          <Badge
            key={c.col}
            size="sm"
            variant="light"
            color="gray"
            style={{ textTransform: 'none' }}
          >
            {c.col}: Σ {num(c.sum)} · {num(c.min)}–{num(c.max)}
          </Badge>
        ))}

        {/* Gaps are worth knowing even where a total is not. */}
        {gaps.map((c) => (
          <Badge
            key={c.col}
            size="sm"
            variant="light"
            color="chartAmber"
            style={{ textTransform: 'none' }}
          >
            {c.col}: {formatNumber(c.empty)} empty
          </Badge>
        ))}
      </Group>
    </Paper>
  );
}
